import React, { useEffect } from 'react';
import { ModalHeader, ModalFooter, ModalWizardBody } from '@panwds/react-ui';
import { Form } from '../Form/Form.esm.js';
import { useFormStatus } from './useFormStatus.esm.js';
import { useWizardContext } from './WizardContext.esm.js';

/**
 * Wizards are multi-step forms for complex workflows.
 *
 * Each of the core steps of a wizard is a form using the `WizardForm`.
 *
 * The `WizardForm` composes the `Form` from this package with the
 * `ModalWizardBody` from `@panwds/react-ui`.
 *
 * The `onNext`, `onPrevious`, and `onSave` callbacks are provided the current step's
 * form values. Store these values, along with those of the other steps in a parent,
 * Wizard component.
 *
 * The navigation items are passed through the `WizardForm` via `navItems`.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { WizardForm, FormLayout } from '@panwds/react-form';
 * import { Modal, ModalWizardBody, ModalWizardNavItem, ModalWizardSummary, ModalWizardStepSummary, ModalHeader } from '@panwds/react-ui';
 * ```
 */
function WizardForm({ actions, activeStepTitle, children, navItems, onSubmit: userOnSubmit, secondaryFooterContent, title: titleOverride, ...formProps }) {
    const { state: { activeIndex, steps, title }, submitStep, } = useWizardContext();
    const onSubmit = userOnSubmit ||
        ((values) => {
            submitStep({ index: activeIndex, values });
        });
    return (React.createElement(Form, { formProps: { className: "tw-flex tw-flex-col tw-flex-auto" }, mode: "onBlur", defaultValues: steps[activeIndex].values, ...formProps, onSubmit: onSubmit },
        React.createElement(ModalHeader, { title: titleOverride || title }),
        React.createElement(ConnectedModalWizardBody, { activeStepTitle: activeStepTitle, navItems: navItems }, children),
        React.createElement(ModalFooter, { secondaryContent: secondaryFooterContent }, actions)));
}
function ConnectedModalWizardBody({ activeStepTitle, children, navItems, }) {
    const status = useFormStatus();
    const wizardContext = useWizardContext();
    const activeIndex = wizardContext.state.activeIndex;
    const activeStep = wizardContext.state.steps[activeIndex];
    useEffect(() => {
        wizardContext.statusUpdate({ index: activeIndex, status });
    }, [activeIndex, status]);
    return (React.createElement(ModalWizardBody, { activeStepTitle: activeStepTitle || activeStep.label, navItems: navItems || wizardContext.navItems }, children));
}

export { WizardForm };
//# sourceMappingURL=WizardForm.esm.js.map
